<script setup lang="ts">
/**
 * AppHeaderMenuMobile — liens « Plateforme » du menu mobile, refaits comme le
 * menu ordinateur (AppHeaderMegaMenu, Karim 2026-11-03) : mêmes sections,
 * mêmes icônes Hugeicons (app/assets/icons/menu-*.svg), en volets qui se
 * déplient au toucher. Un seul volet ouvert à la fois : ouvrir l'un referme
 * l'autre, le menu reste court (Karim : « ya trop sur un seul »).
 *
 * Volets : Choisir Donoor (Karim 2026-11-04 : les cartes du menu ordinateur,
 * en liens), Collecter des dons, Gérer son association, Découvrir, Ressources
 * (cf. `choisirDonoorCartes` et `menuMobileVolets` dans utils/navLinks).
 *
 * Remplace les volets « Collecte / Outils / Découvrir » aux icônes animées
 * (AppHeaderMobileIcon, gardé tel quel mais plus utilisé).
 *
 * Le panneau mobile est monté sur toutes les pages, puis masqué : liens et
 * libellés restent dans le HTML même repliés (v-show), car sur une fiche
 * d'association ce sont les seuls liens de navigation que Google y trouve
 * (cf. note SEO du 2026-09-18 dans l'historique d'AppHeader.vue).
 */
import { choisirDonoorCartes, choisirDonoorInclus, menuMobileVolets } from "~/utils/navLinks";

const { t } = useI18n();
const route = useRoute();

const emit = defineEmits<{
  "nav-click": [lien: { to: string; target?: string }];
  /** Fermer le menu sans changer de page (l'assistant ouvre le chat). */
  fermer: [];
}>();

interface LienVolet {
  labelCle: string;
  icon: string;
  /** Absente pour l'assistant, qui ouvre le chat du site. */
  to?: string;
  target?: string;
}

const volets: { titreCle: string; liens: LienVolet[] }[] = [
  {
    titreCle: "navigation.appHeader.choisirDonoor",
    liens: [...choisirDonoorCartes, ...choisirDonoorInclus].map((carte) => ({
      labelCle: `navigation.appHeader.choisir.${carte.cle}Titre`,
      icon: carte.icon,
      to: carte.to,
    })),
  },
  ...menuMobileVolets,
];

const questionPourChat = useQuestionPourChat();

function ouvrirAssistant() {
  // Ouvre le chat du site, champ vide (cf. PublicChatWidget).
  questionPourChat.value = "";
  emit("fermer");
}

const voletOuvert = ref<string | null>(null);

function basculer(titreCle: string) {
  voletOuvert.value = voletOuvert.value === titreCle ? null : titreCle;
}

const idBase = useId();

function estActif(to: string) {
  return route.path === to;
}
</script>

<template>
  <div class="flex flex-col">
    <div v-for="(volet, rangVolet) in volets" :key="volet.titreCle" class="flex flex-col">
      <button
        type="button"
        class="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left font-bold text-gray-900 transition-colors hover:bg-gray-50"
        :aria-expanded="voletOuvert === volet.titreCle"
        :aria-controls="`${idBase}-${rangVolet}`"
        @click="basculer(volet.titreCle)"
      >
        <span>{{ t(volet.titreCle) }}</span>
        <svg
          class="h-4 w-4 text-gray-400 transition-transform duration-200"
          :class="{ 'rotate-180': voletOuvert === volet.titreCle }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 10 6"
          aria-hidden="true"
        >
          <path
            d="M1.667 1.667L5.001 4.72L8.334 1.667"
            stroke-width="1.33"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <!-- Dépliage en hauteur 0fr → 1fr. L'élément ne porte que `grid` : les
           valeurs de hauteur vivent seulement dans les classes de la
           transition, sinon une classe fixe l'emporterait et la hauteur
           sauterait d'un coup. Ouverture qui part vite et se pose, repli qui
           accélère puis ralentit, comme le menu ordinateur. Quand on passe
           d'un volet à l'autre, les deux mouvements se font en même temps. -->
      <Transition
        enter-active-class="transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
        enter-from-class="grid-rows-[0fr] opacity-0"
        enter-to-class="grid-rows-[1fr] opacity-100"
        leave-active-class="transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
        leave-from-class="grid-rows-[1fr] opacity-100"
        leave-to-class="grid-rows-[0fr] opacity-0"
      >
        <div v-show="voletOuvert === volet.titreCle" :id="`${idBase}-${rangVolet}`" class="grid">
          <!-- La marge du bas vit DANS la boîte qui se replie : posée sur
               elle, elle resterait visible jusqu'à la fin du repli. -->
          <div class="overflow-hidden">
            <ul class="flex flex-col pb-2">
              <li
                v-for="(lien, rangLien) in volet.liens"
                :key="lien.labelCle"
                class="mobile-ligne"
                :style="{ '--rang': rangLien }"
              >
                <NuxtLink
                  v-if="lien.to"
                  :to="lien.to"
                  class="mobile-lien"
                  :aria-current="estActif(lien.to) ? 'page' : undefined"
                  @click.prevent="emit('nav-click', { to: lien.to, target: lien.target })"
                >
                  <UIcon :name="lien.icon" class="mobile-icone" />
                  <span class="mobile-libelle">{{ t(lien.labelCle) }}</span>
                </NuxtLink>
                <button v-else type="button" class="mobile-lien w-full text-left" @click="ouvrirAssistant">
                  <UIcon :name="lien.icon" class="mobile-icone" />
                  <span class="mobile-libelle">{{ t(lien.labelCle) }}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
/* Lignes de 44 px, la taille d'un doigt. La couleur est portée par le lien :
   l'icône la suit (currentColor). Libellé et icône aux mesures du menu
   ordinateur. */
.mobile-lien {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.625rem 1rem;
  border-radius: 0.75rem;
  color: #0f0f0f;
  transition:
    background-color 200ms ease,
    color 200ms ease;
}
.mobile-lien:active {
  background-color: #f9fafb;
}
@media (hover: hover) {
  .mobile-lien:hover {
    background-color: #f9fafb;
  }
}
/* Page en cours : le lien prend le bleu de marque. */
.mobile-lien[aria-current="page"] {
  color: var(--color-main);
}

.mobile-icone {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.mobile-libelle {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5rem;
}

/* Dépliage : les liens apparaissent l'un après l'autre en montant de
   quelques pixels, comme les colonnes du menu ordinateur. Le volet passe de
   masqué à affiché à chaque ouverture : l'animation est rejouée à chaque
   fois. */
.mobile-ligne {
  animation: mobile-entree 340ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: calc(var(--rang, 0) * 30ms);
}

@keyframes mobile-entree {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mobile-ligne {
    animation: none;
  }
  .mobile-lien {
    transition: none;
  }
}
</style>
